import { useCallback } from "react";
import {
  fetchAIResponse,
  getObjectsForIDs,
  getTheoryForID,
  getEducationById,
  getRolePlaysForTheory,
  getBranchingStoriesForTheory,
} from "../../Models/firebaseHelper";
import { GenericLevelId, LevelType, TheoryTypes } from "./Education.types";
import { QUESTIONS_PER_SUBTEST_OVERRIDE } from "../../constants";

export function useFetchCourseData(courseId: string | undefined) {
  const fetchTheory = useCallback(async (theoryId: GenericLevelId) => {
    if (typeof theoryId !== "string") {
      return null;
    }

    const theory = await getTheoryForID(theoryId);
    return theory?.HtmlString;
  }, []);

  const fetchAIChat = useCallback(
    async (aiChatId: GenericLevelId) => {
      if (typeof aiChatId !== "string") {
        return null;
      }

      const theoryData = await getTheoryForID(aiChatId);
      const html = theoryData?.HtmlString;
      const aiChatSummary = theoryData?.AIChatSummary;

      const aiInput = {
        messages: [{ role: "user", content: "" }],
        studyText: html,
        aiChatSummary,
        educationId: courseId,
      };

      const firstAIResponse = await fetchAIResponse(aiInput, () => {});

      return {
        html,
        aiChatSummary,
        initialResponse: firstAIResponse,
      };
    },
    [courseId]
  );

  const fetchNormalQuestions = useCallback(
    async (questionIds: GenericLevelId) => {
      return await getObjectsForIDs("questions", questionIds);
    },
    []
  );

  const fetchLoopQuestions = useCallback(
    async (questionLoopIds: GenericLevelId) => {
      if (typeof questionLoopIds !== "object" || questionLoopIds === null) {
        return null;
      }

      // Fetch all questions for the loop at once
      let promises = [];

      for (let i = 0; i < questionLoopIds.length; i++) {
        promises.push(getObjectsForIDs("questions", questionLoopIds[i]));
      }

      const result = await Promise.all(promises);
      const flatResult = result.flat();

      return flatResult;
    },
    []
  );

  const fetchQuizQuestions = useCallback(
    async (quizQuestionIds: GenericLevelId) => {
      // If we already have a specific list of question IDs, apply the per-education limit
      let ids = Array.isArray(quizQuestionIds)
        ? [...quizQuestionIds]
        : quizQuestionIds;

      if (Array.isArray(ids)) {
        let questionsPerSubtest = 10;

        // 1) Highest priority: environment override (centralized constant)
        if (typeof QUESTIONS_PER_SUBTEST_OVERRIDE === "number") {
          questionsPerSubtest = QUESTIONS_PER_SUBTEST_OVERRIDE;
        } else {
          // 2) Fallback: per-education config fetched from Firestore
          try {
            if (courseId) {
              const education = await getEducationById(courseId);
              const configured = (education as any)?.QuestionsPerSubtest as
                | number
                | string
                | undefined;
              if (configured != null) {
                const numeric =
                  typeof configured === "string"
                    ? parseInt(configured, 10)
                    : Number(configured);
                if (!Number.isNaN(numeric) && Number.isFinite(numeric)) {
                  questionsPerSubtest = Math.min(100, Math.max(1, numeric));
                }
              }
            }
          } catch (e) {
            // ignore and use default
          }
        }

        // Randomize and pick N
        const shuffled = ids.sort(() => 0.5 - Math.random());
        ids = shuffled.slice(0, questionsPerSubtest);
      }

      return await getObjectsForIDs("questions", ids);
    },
    [courseId]
  );

  const fetchWordlists = useCallback(async (wordlistIds: GenericLevelId) => {
    return await getObjectsForIDs("wordlists", wordlistIds);
  }, []);

  const fetchCourseData = useCallback(
    async (selectedLevelType: LevelType, ids: GenericLevelId) => {
      if (ids === null || ids === undefined) {
        return null;
      }

      let result;

      if (selectedLevelType === TheoryTypes.THEORY) {
        result = await fetchTheory(ids);
      } else if (selectedLevelType === TheoryTypes.NORMAL) {
        result = await fetchNormalQuestions(ids);
      } else if (selectedLevelType === TheoryTypes.LOOPS) {
        result = await fetchLoopQuestions(ids);
      } else if (selectedLevelType === TheoryTypes.QUIZ) {
        result = await fetchQuizQuestions(ids);
      } else if (selectedLevelType === TheoryTypes.WORDLISTS) {
        result = await fetchWordlists(ids);
      } else if (selectedLevelType === TheoryTypes.CHAT) {
        result = await fetchAIChat(ids);
      } else if (selectedLevelType === TheoryTypes.DIAGNOSTIC_TEST) {
        result = await fetchNormalQuestions(ids);
      } else if (selectedLevelType === TheoryTypes.ROLEPLAY) {
        // ids can be either theoryId or [theoryId, rolePlayId]
        let theoryId: string | null = null;
        let rolePlayId: string | null = null;

        if (Array.isArray(ids)) {
          theoryId = ids[0] ?? null;
          rolePlayId = ids[1] ?? null;
        } else if (typeof ids === "string") {
          theoryId = ids;
        }

        const theory = await fetchTheory(theoryId);
        let rolePlay: any = undefined;
        if (rolePlayId) {
          const arr = await getObjectsForIDs("roleplays", [rolePlayId]);
          rolePlay = arr?.[0];
        } else if (theoryId) {
          const arr = await getRolePlaysForTheory(theoryId);
          rolePlay = arr?.[0];
        }
        result = { html: theory, rolePlay } as any;
      } else if (selectedLevelType === TheoryTypes.BRANCHINGSTORY) {
        // ids can be either theoryId or [theoryId, branchingStoryId]
        let theoryId: string | null = null;
        let branchingStoryId: string | null = null;

        if (Array.isArray(ids)) {
          theoryId = ids[0] ?? null;
          branchingStoryId = ids[1] ?? null;
        } else if (typeof ids === "string") {
          theoryId = ids;
        }

        const theory = await fetchTheory(theoryId);
        let branchingStory: any = undefined;
        if (branchingStoryId) {
          console.log(
            "[useFetchCourseData] Fetching branching story by ID:",
            branchingStoryId
          );
          const arr = await getObjectsForIDs("branchingStories", [
            branchingStoryId,
          ]);
          branchingStory = arr?.[0];
          console.log("[useFetchCourseData] Fetched branching story by ID:", {
            found: !!branchingStory,
            id: branchingStory?.ID,
            title: branchingStory?.Title,
            nodesCount: branchingStory?.Nodes?.length,
          });

          // Fallback: if not found by ID, try fetching by theoryId
          if (!branchingStory && theoryId) {
            console.log(
              "[useFetchCourseData] Story not found by ID, trying theoryId fallback:",
              theoryId
            );
            const arr = await getBranchingStoriesForTheory(theoryId);
            branchingStory = arr?.[0];
            console.log(
              "[useFetchCourseData] Fetched branching stories by theoryId:",
              {
                count: arr.length,
                found: !!branchingStory,
                id: branchingStory?.ID,
                title: branchingStory?.Title,
                nodesCount: branchingStory?.Nodes?.length,
              }
            );
          }
        } else if (theoryId) {
          console.log(
            "[useFetchCourseData] Fetching branching stories for theory:",
            theoryId
          );
          const arr = await getBranchingStoriesForTheory(theoryId);
          branchingStory = arr?.[0];
          console.log("[useFetchCourseData] Fetched branching stories:", {
            count: arr.length,
            found: !!branchingStory,
            id: branchingStory?.ID,
            title: branchingStory?.Title,
            nodesCount: branchingStory?.Nodes?.length,
          });
        }
        result = { html: theory, branchingStory } as any;
        console.log("[useFetchCourseData] Returning result:", {
          hasHtml: !!result.html,
          hasBranchingStory: !!result.branchingStory,
        });
      } else if (selectedLevelType === TheoryTypes.VIDEO) {
        result = null;
      }

      return result;
    },
    [
      fetchTheory,
      fetchAIChat,
      fetchNormalQuestions,
      fetchLoopQuestions,
      fetchQuizQuestions,
      fetchWordlists,
    ]
  );

  return { fetchCourseData };
}
